/**
 * F33 — Admin "Trainer certifications" queue (renamed by the F09 v2 merge).
 *
 * Route: /trainer-verifications
 * Permission: trainer_verifications:review (super_admin + content_moderator)
 *
 * The FIFO pending-CERT queue: trainers with at least one `pending`
 * certification, oldest pending cert first. F09 v2 retired the F33 profile
 * verification (the trainer tick is now an F09 request decided under
 * `/verifications`), so the queue has no status filter and no profile
 * approve/reject. Each row shows the trainer's tick (inert) when they hold it.
 *
 * The page itself does NOT poll for the sidenav badge — that lives in
 * `NavCountsProvider` and is invoked once per app shell.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, EmptyState, Skeleton, Table, type TableColumn } from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { AdminPageMeta } from '@bravemove/types';
import {
  adminTrainerVerificationsApi,
  type TrainerVerificationQueueItem,
  type TrainerVerificationsListOpts,
} from '../../api/trainerVerifications';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';
import { InertTick } from '../../components/verification/EntityVerificationCard';

const PAGE_LIMIT = 25;

function formatRelative(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '—';
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

export function TrainerVerificationsQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly TrainerVerificationQueueItem[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: TrainerVerificationsListOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminTrainerVerificationsApi.list(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList({ perPage: PAGE_LIMIT, page: pageParam });
  }, [fetchList, pageParam]);

  const goToPage = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const columns = useMemo<readonly TableColumn<TrainerVerificationQueueItem>[]>(
    () => [
      {
        key: 'trainer',
        header: t('admin:trainer-verification.queue.column_trainer'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5 font-medium text-[13px] text-ink">
              {row.displayName}
              <InertTick
                verified={row.verified}
                entityType="trainer"
                testId={`trainer-cert-queue-tick-${row.userId}`}
              />
            </span>
            {row.email ? (
              <span className="font-mono text-[11px] text-ink-3">{row.email}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'headline',
        header: t('admin:trainer-verification.queue.column_headline'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 line-clamp-1">{row.headline ?? '—'}</span>
        ),
      },
      {
        key: 'certs',
        header: t('admin:trainer-verification.queue.column_certs'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 font-mono tabular-nums">
            {row.pendingCertCount}/{row.certCount}
          </span>
        ),
      },
      {
        key: 'oldestPending',
        header: t('admin:trainer-verification.queue.column_oldest_pending'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {formatRelative(row.oldestPendingCertAt ?? row.createdAt, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="trainer-verifications-page">
      <PageHeader
        title={t('admin:trainer-verification.queue.title')}
        subtitle={t('admin:trainer-verification.queue.subtitle')}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="trainer-verifications-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="shield" size={36} color="currentColor" />}
          title={t('admin:trainer-verification.queue.empty_title')}
          description={t('admin:trainer-verification.queue.empty_body')}
        />
      ) : (
        <Table
          data={rows as TrainerVerificationQueueItem[]}
          columns={columns as TableColumn<TrainerVerificationQueueItem>[]}
          rowKey={(row) => row.userId}
          onRowPress={(row) => navigate(`/trainer-verifications/${row.userId}`)}
          empty={t('admin:trainer-verification.queue.empty_title')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="trainer-verifications-pagination"
        />
      ) : null}
    </div>
  );
}

export default TrainerVerificationsQueuePage;
